import React, { useEffect, useRef, useState, useMemo } from 'react';
import {
    Terminal, Pause, Play, Trash2, XCircle,
    WifiOff, Download, Filter
} from 'lucide-react';
import AnimatedBackground from '../components/Background';
import { ensureSocketConnection } from '../websocket/socketUtils';
import { useWebSocket } from '../websocket/WebSocketContext';

const LOG_LEVELS = ['DEBUG', 'INFO', 'WARN', 'ERROR'];
const LOG_LIMIT_OPTIONS = [1000, 2000, 3000, 5000, 8000, 10000];

const levelColorMap = {
    DEBUG: 'text-blue-400',
    INFO: 'text-green-400',
    WARN: 'text-yellow-400',
    ERROR: 'text-red-400'
};

function toggleLevel(level, set, setter) {
    const copy = new Set(set);
    copy.has(level) ? copy.delete(level) : copy.add(level);
    setter(copy);
}

function parseLog(raw) {
    const levelMatch = raw.match(/\b(INFO|WARN|ERROR|DEBUG)\b/);
    const moduleMatch = raw.match(/-\s*([A-Za-z0-9_.$]+)\s*:/);
    return {
        raw,
        level: levelMatch ? levelMatch[1] : 'INFO',
        module: moduleMatch ? moduleMatch[1] : '',
        id: `${Date.now()}-${Math.random()}`,
        isNew: true
    };
}

function extractLevel(line) {
    const match = line.match(/\b(INFO|WARN|ERROR|DEBUG)\b/);
    return match ? match[1] : 'INFO';
}

function extractModule(line) {
    const match = line.match(/-\s*([A-Za-z0-9_.$]+)\s*:/);
    return match ? match[1] : '';
}

export default function Logs({ onBack, loginId, deliveryMode }) {
    const { client } = useWebSocket();

    const [logs, setLogs] = useState([]);
    const [paused, setPaused] = useState(false);
    const [connected, setConnected] = useState(false);

    const [mode, setMode] = useState('LIVE');
    const [searching, setSearching] = useState(false);
    const [progress, setProgress] = useState(0);
    const [searchRequestId, setSearchRequestId] = useState(null);

    const [levelFilter, setLevelFilter] = useState(new Set(LOG_LEVELS));
    const [searchPattern, setSearchPattern] = useState('');
    const [filterText, setFilterText] = useState('');
    const [moduleFilter, setModuleFilter] = useState('');
    const [logLimit, setLogLimit] = useState(3000);
    const [showLimitDropdown, setShowLimitDropdown] = useState(false);

    const [availableFiles, setAvailableFiles] = useState([]);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [showFileDropdown, setShowFileDropdown] = useState(false);
    const [loadingFiles, setLoadingFiles] = useState(false);

    const logsEndRef = useRef(null);
    const liveSubRef = useRef(null);
    const searchSubRef = useRef(null);
    const fileNameSubRef = useRef(null);
    const fileDropdownRef = useRef(null);
    const limitDropdownRef = useRef(null);

    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            @keyframes slideIn {
                from { opacity: 0; transform: translateX(-10px); }
                to { opacity: 1; transform: translateX(0); }
            }
            .animate-slideIn { animation: slideIn 0.5s ease-out forwards; }
        `;
        document.head.appendChild(style);
        return () => document.head.removeChild(style);
    }, []);

    useEffect(() => {
        function handleClickOutside(event) {
            if (fileDropdownRef.current && !fileDropdownRef.current.contains(event.target))
                setShowFileDropdown(false);
            if (limitDropdownRef.current && !limitDropdownRef.current.contains(event.target))
                setShowLimitDropdown(false);
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (deliveryMode !== 'socket') return;
        ensureSocketConnection(client)
            .then(() => setConnected(!!client?.connected))
            .catch(() => setConnected(false));
    }, [client, deliveryMode]);

    useEffect(() => {
        if (!paused && logsEndRef.current)
            logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }, [logs, paused]);

    useEffect(() => {
        if (deliveryMode !== 'socket' || !client?.connected) return;
        setLoadingFiles(true);
        fileNameSubRef.current = client.subscribe(
            `/topic/filename/logs/${loginId}`,
            msg => {
                try {
                    const response = JSON.parse(msg.body);
                    if (Array.isArray(response)) {
                        setAvailableFiles(response);
                        if (response.length > 0) setSelectedFiles([response[0]]);
                    }
                } finally { setLoadingFiles(false); }
            }
        );
        client.publish({ destination: '/app/filename/logs', body: JSON.stringify({}) });
        return () => fileNameSubRef.current?.unsubscribe();
    }, [client?.connected, loginId, deliveryMode]);

    useEffect(() => {
        if (mode !== 'LIVE' || !client?.connected) {
            liveSubRef.current?.unsubscribe();
            liveSubRef.current = null;
            return;
        }
        liveSubRef.current = client.subscribe(
            `/topic/logs/${loginId}`,
            msg => {
                if (paused) return;
                const newLog = parseLog(msg.body);
                setLogs(prev => {
                    const next = [...prev, newLog];
                    return next.length > logLimit ? next.slice(next.length - logLimit) : next;
                });
                setTimeout(() => {
                    setLogs(current => current.map((log, idx) =>
                        idx === current.length - 1 ? { ...log, isNew: false } : log
                    ));
                }, 600);
            }
        );
        return () => liveSubRef.current?.unsubscribe();
    }, [client?.connected, mode, loginId, paused, logLimit]);

    useEffect(() => {
        if (mode !== 'SEARCH' || !client?.connected) {
            searchSubRef.current?.unsubscribe();
            searchSubRef.current = null;
            return;
        }
        searchSubRef.current = client.subscribe(
            `/topic/previous/logs/${loginId}`,
            msg => {
                const data = JSON.parse(msg.body);
                switch (data.type) {
                    case 'LOG_SEARCH_RESULT':
                        if (data.lines && Array.isArray(data.lines)) {
                            const parsedLines = data.lines.map((logLine, index) => ({
                                raw: logLine.line,
                                level: extractLevel(logLine.line),
                                module: extractModule(logLine.line),
                                lineNumber: logLine.lineNumber,
                                timestamp: logLine.timestamp,
                                id: `${Date.now()}-${index}-${Math.random()}`,
                                isNew: true
                            }));
                            setLogs(prev => [...prev, ...parsedLines]);
                            setTimeout(() => {
                                setLogs(current => current.map(log => ({ ...log, isNew: false })));
                            }, 600);
                        }
                        setSearching(false);
                        break;
                    case 'LOG_SEARCH_ERROR':
                        alert(data.message);
                        setSearching(false);
                        break;
                }
            }
        );
        return () => searchSubRef.current?.unsubscribe();
    }, [client?.connected, mode, loginId]);

    function startSearch() {
        if (!client?.connected || !searchSubRef.current) return;
        const requestId = crypto.randomUUID();
        setSearchRequestId(requestId);
        setSearching(true);
        setProgress(0);
        setLogs([]);
        const selectedLevels = Array.from(levelFilter);
        const level = selectedLevels.length === 1 ? selectedLevels[0] : null;
        client.publish({
            destination: '/app/previous/logs',
            body: JSON.stringify({
                type: 'LOG_SEARCH', requestId,
                file: selectedFiles, pattern: searchPattern || null,
                regex: false, ignoreCase: true, level,
                before: 2, after: 2, limit: logLimit, from: null, to: null
            })
        });
    }

    function toggleFile(file) {
        setSelectedFiles(prev =>
            prev.includes(file) ? prev.filter(f => f !== file) : [...prev, file]
        );
    }

    const filteredLogs = useMemo(() => {
        return logs.filter(log => {
            if (!levelFilter.has(log.level)) return false;
            if (filterText && !log.raw.toLowerCase().includes(filterText.toLowerCase())) return false;
            if (moduleFilter && !log.module.toLowerCase().includes(moduleFilter.toLowerCase())) return false;
            return true;
        });
    }, [logs, levelFilter, filterText, moduleFilter]);

    const downloadLogs = () => {
        const content = filteredLogs.map(l => l.raw).join('\n');
        const blob = new Blob([content], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `logs-${loginId}.log`;
        a.click();
        URL.revokeObjectURL(url);
    };

    if (deliveryMode !== 'socket') {
        return (
            <div className="flex items-center justify-center h-full bg-slate-950 text-white">
                <AnimatedBackground />
                <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-8 sm:p-10 text-center max-w-md mx-4">
                    <WifiOff className="w-12 h-12 text-red-400 mx-auto mb-4" />
                    <h2 className="text-xl sm:text-2xl font-bold mb-2">Socket Mode Required</h2>
                    <p className="text-white/70 mb-6 text-sm sm:text-base">
                        Live logs are available only in WebSocket mode.
                    </p>
                    <button onClick={onBack} className="px-6 py-2 bg-purple-500/30 hover:bg-purple-500/40 rounded-lg">
                        Go Back
                    </button>
                </div>
            </div>
        );
    }

    return (
        // KEY FIX: flex flex-col h-full instead of min-h-screen
        <div className="flex flex-col h-full bg-slate-950 text-white font-mono overflow-hidden">

            {/* HEADER — shrink-0 so it never compresses */}
            <header className="shrink-0 bg-black/50 backdrop-blur border-b border-white/20 z-40">
                <div className="flex items-center justify-between px-3 sm:px-6 h-14 sm:h-16">
                    <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
                        <Terminal className="text-green-400 shrink-0 w-4 h-4 sm:w-5 sm:h-5" />
                        <div className="min-w-0">
                            <h1 className="font-bold text-sm sm:text-base truncate">Live Server Logs</h1>
                            <p className="text-xs text-white/60">{connected ? 'Connected' : 'Disconnected'}</p>
                        </div>
                        {connected && mode === 'LIVE' && (
                            <span className="hidden sm:flex items-center text-xs text-green-400 ml-2">
                                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse mr-1" />
                                LIVE
                            </span>
                        )}
                    </div>
                    <div className="flex items-center space-x-1 sm:space-x-2 shrink-0">
                        <button onClick={() => setPaused(!paused)}
                            className="p-2 sm:px-3 sm:py-1.5 bg-white/10 hover:bg-white/20 rounded border border-white/20 transition-colors">
                            {paused ? <Play size={14} /> : <Pause size={14} />}
                        </button>
                        <button onClick={() => setLogs([])}
                            className="p-2 sm:px-3 sm:py-1.5 bg-white/10 hover:bg-white/20 rounded border border-white/20 transition-colors">
                            <Trash2 size={14} />
                        </button>
                        <button onClick={downloadLogs}
                            className="p-2 sm:px-3 sm:py-1.5 bg-white/10 hover:bg-white/20 rounded border border-white/20 transition-colors">
                            <Download size={14} />
                        </button>
                        <button onClick={onBack}
                            className="p-2 sm:px-3 sm:py-1.5 bg-white/10 hover:bg-white/20 rounded border border-white/20 transition-colors">
                            <XCircle size={14} />
                        </button>
                    </div>
                </div>
            </header>

            {/* MODE SWITCH — shrink-0 */}
            <div className="shrink-0 px-3 sm:px-6 pt-3 pb-2 flex gap-2 bg-black/40 border-b border-white/10">
                <button
                    onClick={() => { setMode('LIVE'); setLogs([]); }}
                    className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded text-xs sm:text-sm font-medium transition-colors ${mode === 'LIVE' ? 'bg-purple-500/40 text-white' : 'bg-white/10 text-white/60 hover:bg-white/20'}`}>
                    Live Logs
                </button>
                <button
                    onClick={() => { setMode('SEARCH'); setLogs([]); setSearching(false); }}
                    className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded text-xs sm:text-sm font-medium transition-colors ${mode === 'SEARCH' ? 'bg-purple-500/40 text-white' : 'bg-white/10 text-white/60 hover:bg-white/20'}`}>
                    Previous Logs
                </button>
            </div>

            {/* SEARCH CONTROLS — shrink-0, only in SEARCH mode */}
            {mode === 'SEARCH' && (
                <div className="shrink-0 px-3 sm:px-6 py-3 bg-black/40 border-b border-white/10">
                    <div className="flex gap-2 sm:gap-3 items-center flex-wrap">
                        {/* File dropdown */}
                        <div className="relative" ref={fileDropdownRef}>
                            <button
                                onClick={() => setShowFileDropdown(!showFileDropdown)}
                                disabled={loadingFiles || availableFiles.length === 0}
                                className={`px-3 py-1.5 text-xs sm:text-sm rounded bg-black/40 border border-white/20 hover:bg-white/10 flex items-center gap-2 ${loadingFiles || availableFiles.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                <span className="text-white/80">
                                    {loadingFiles ? 'Loading...' : availableFiles.length === 0 ? 'No files' : `Files: ${selectedFiles.length || 'None'}`}
                                </span>
                                <span className="text-white/60">▼</span>
                            </button>
                            {showFileDropdown && availableFiles.length > 0 && (
                                <div className="absolute z-50 mt-2 bg-slate-900 border border-white/20 rounded-lg shadow-lg overflow-hidden min-w-[220px] sm:min-w-[250px]">
                                    <div className="px-4 py-2 bg-purple-500/20 border-b border-white/10 flex items-center justify-between">
                                        <span className="text-xs text-purple-200 font-medium">{selectedFiles.length} / {availableFiles.length} selected</span>
                                        {selectedFiles.length > 0 && (
                                            <button onClick={e => { e.stopPropagation(); setSelectedFiles([]); }} className="text-xs text-red-300 hover:text-red-200">Clear</button>
                                        )}
                                    </div>
                                    {availableFiles.map(file => (
                                        <button key={file} onClick={() => toggleFile(file)}
                                            className={`block w-full px-4 py-2 text-left text-xs sm:text-sm hover:bg-purple-500/20 flex items-center gap-2 ${selectedFiles.includes(file) ? 'bg-purple-500/30 text-purple-200' : 'text-white/70'}`}>
                                            <span className={`w-4 h-4 border rounded flex items-center justify-center shrink-0 ${selectedFiles.includes(file) ? 'border-purple-400 bg-purple-500/40' : 'border-white/40'}`}>
                                                {selectedFiles.includes(file) && <span className="text-purple-200 text-xs">✓</span>}
                                            </span>
                                            <span className="truncate">{file}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        <input
                            placeholder="Search pattern…"
                            className="flex-1 min-w-[120px] bg-black/40 border border-white/20 rounded px-3 py-1.5 text-xs sm:text-sm focus:outline-none focus:border-purple-500/50"
                            value={searchPattern}
                            onChange={e => setSearchPattern(e.target.value)}
                            onKeyPress={e => { if (e.key === 'Enter' && !searching && selectedFiles.length > 0) startSearch(); }}
                        />

                        <button onClick={startSearch} disabled={searching || selectedFiles.length === 0}
                            className={`px-3 sm:px-4 py-1.5 rounded text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${searching || selectedFiles.length === 0 ? 'bg-white/10 text-white/40 cursor-not-allowed' : 'bg-purple-500/40 hover:bg-purple-500/50 text-white'}`}>
                            {searching ? 'Searching…' : 'Search'}
                        </button>

                        {searching && (
                            <button className="px-3 sm:px-4 py-1.5 bg-red-500/30 hover:bg-red-500/40 rounded text-xs sm:text-sm text-red-200 transition-colors">
                                Cancel
                            </button>
                        )}

                        {logs.length > 0 && !searching && (
                            <div className="ml-auto px-3 py-1.5 bg-green-500/20 border border-green-500/30 rounded-lg flex items-center gap-2">
                                <span className="text-green-300 font-bold text-sm sm:text-lg">{logs.length}</span>
                                <span className="text-green-200 text-xs">matches</span>
                            </div>
                        )}
                    </div>

                    {searching && (
                        <div className="w-full bg-white/10 h-1.5 rounded mt-3 overflow-hidden">
                            <div className="bg-purple-500 h-1.5 rounded transition-all duration-300 animate-pulse" style={{ width: `${Math.max(10, progress)}%` }} />
                        </div>
                    )}
                </div>
            )}

            {/* FILTER BAR — shrink-0 */}
            <div className="shrink-0 px-3 sm:px-4 py-2 sm:py-3 flex flex-wrap gap-2 sm:gap-3 bg-black/40 border-b border-white/10 items-center">
                <div className="flex gap-1 sm:gap-2 items-center flex-wrap">
                    <Filter size={12} className="text-white/60 shrink-0" />
                    {LOG_LEVELS.map(lvl => (
                        <button key={lvl}
                            onClick={() => toggleLevel(lvl, levelFilter, setLevelFilter)}
                            className={`px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-xs ${levelFilter.has(lvl) ? 'bg-purple-500/40' : 'bg-white/10'}`}>
                            {lvl}
                        </button>
                    ))}
                </div>

                <input
                    placeholder="Filter results…"
                    className="flex-1 min-w-[100px] bg-black/40 border border-white/20 rounded px-2 sm:px-3 py-1 text-xs sm:text-sm focus:outline-none focus:border-purple-500/50"
                    value={filterText}
                    onChange={e => setFilterText(e.target.value)}
                />

                <input
                    placeholder="Filter by module…"
                    className="flex-1 min-w-[100px] bg-black/40 border border-white/20 rounded px-2 sm:px-3 py-1 text-xs sm:text-sm focus:outline-none focus:border-purple-500/50"
                    value={moduleFilter}
                    onChange={e => setModuleFilter(e.target.value)}
                />

                {/* Limit dropdown */}
                <div className="relative" ref={limitDropdownRef}>
                    <button onClick={() => setShowLimitDropdown(!showLimitDropdown)}
                        className="px-2 sm:px-3 py-1 text-xs rounded bg-black/40 border border-white/20 hover:bg-white/10 flex items-center gap-1 whitespace-nowrap">
                        Lines: {logLimit} <span className="text-white/60">▼</span>
                    </button>
                    {showLimitDropdown && (
                        <div className="absolute z-50 mt-2 bg-slate-900 border border-white/20 rounded-lg shadow-lg overflow-hidden right-0">
                            {LOG_LIMIT_OPTIONS.map(limit => (
                                <button key={limit}
                                    onClick={() => {
                                        setLogLimit(limit);
                                        setShowLimitDropdown(false);
                                        setLogs(prev => prev.length > limit ? prev.slice(prev.length - limit) : prev);
                                    }}
                                    className={`block w-full px-4 py-2 text-left text-xs hover:bg-purple-500/20 whitespace-nowrap ${logLimit === limit ? 'bg-purple-500/30 text-purple-200' : 'text-white/70'}`}>
                                    {limit} lines
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div className="ml-auto flex items-center gap-2">
                    {filteredLogs.length !== logs.length && logs.length > 0 && (
                        <div className="px-2 sm:px-3 py-1 bg-blue-500/20 border border-blue-500/30 rounded text-xs">
                            <span className="text-blue-300">Showing: </span>
                            <span className="text-blue-200 font-bold">{filteredLogs.length}</span>
                            <span className="text-blue-300"> / {logs.length}</span>
                        </div>
                    )}
                    {logs.length > 0 && (
                        <div className="px-2 sm:px-3 py-1 bg-purple-500/20 border border-purple-500/30 rounded text-xs">
                            <span className="text-purple-300">Total: </span>
                            <span className="text-purple-200 font-bold">{logs.length}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* LOG VIEW — flex-1 + min-h-0 is the key to filling remaining space */}
            <div className="flex-1 min-h-0 p-2 sm:p-4">
                <div className="bg-black/70 border border-white/20 rounded-xl p-3 sm:p-4 h-full overflow-y-auto">
                    {filteredLogs.length === 0 ? (
                        <div className="text-white/40 text-sm">Waiting for logs…</div>
                    ) : (
                        filteredLogs.map((log, idx) => (
                            <div key={log.id}
                                className={`whitespace-pre-wrap text-xs sm:text-sm leading-relaxed transition-all duration-500 ${log.isNew ? 'animate-slideIn opacity-0' : 'opacity-100'} ${levelColorMap[log.level]}`}
                                style={{ animationDelay: log.isNew ? `${(idx % 10) * 30}ms` : '0ms' }}>
                                {log.raw}
                            </div>
                        ))
                    )}
                    <div ref={logsEndRef} />
                </div>
            </div>
        </div>
    );
}